import React, { useState } from "react";
import type { FlexProps } from "@chakra-ui/react";
import { Button, Flex, Icon, Spacer, Table, TableContainer, Tbody, Td, Text, Th, Tr, useMediaQuery, useTheme } from "@chakra-ui/react";
import { firstColumn, mobileAfter, mobileProps } from "./FxPerformanceTablePanel";
import { AiOutlineDownload } from "react-icons/ai";
import { AccessibleSelect } from "@/components/AccessibleSelect/AccessibleSelect";
import { HypotheticalValue } from "@/components/HypertheticalValue/HypotheticalValue";
import { useInstanceConfig } from "@/services/InstanceConfigContext";
import { htmlify, interpolatePlaceholders } from "@/services/utils";
import type { FundDetailLayoutComponentProps } from "@/types/fundDetailLayout";

export interface FxPerformancePriipsScenariosPanelProps extends FlexProps {
	periodOptions: {
		value: string;
		label: string;
	}[];
	investmentLabel: string;
	investmentValue: string;
	periodLabel: string;
	recommendedHoldingPeriodLabel: string;
	scenariosLabel: string;
	minimumLabel: string;
	minimumDisclaimer: string;
	downloadDataLabel?: string;
	disclaimer: string;
	sections: {
		recommendedHoldingPeriod: string;
		scenariosItems: string[];
		categoryItems: {
			value: string;
			label: string;
			rows: string[][];
		}[];
	}[];
	downloadUrl: string;
	selectLabel: string;
}

export const FxPerformancePriipsScenariosPanel = ({ dashboard }: FundDetailLayoutComponentProps) => {
	const instanceConfig = useInstanceConfig();
	const theme = useTheme();
	const [isMobile] = useMediaQuery(`(max-width: ${theme?.breakpoints?.md || 320})`);

	const { fxPerformancePriipsScenariosPanel: props } = dashboard;
	const {
		disclaimer,
		downloadDataLabel,
		periodOptions,
		sections,
		investmentLabel,
		investmentValue,
		periodLabel,
		recommendedHoldingPeriodLabel,
		scenariosLabel,
		minimumLabel,
		minimumDisclaimer,
		downloadUrl,
		selectLabel
	} = props || {};

	const [period, setPeriod] = useState<string | undefined>(() => {
		return periodOptions?.[0]?.value;
	});
	const [section, setSection] = useState<FxPerformancePriipsScenariosPanelProps["sections"][0] | undefined>(() => {
		return sections?.[0];
	});
	if (!props || !section) {
		return null;
	}

	const handleDownloadData = () => {
		window.open(downloadUrl);
	};
	return (
		<Flex
			id="fxPerformancePriipsScenariosPanel"
			data-testid="FxPerformancePriipsScenariosPanel"
			direction="column"
			gap={0}
			paddingBottom="20px"
		>
			{isMobile ? (
				<Flex
					direction="column"
					alignItems="baseline"
					fontSize={{ base: "14px", md: "inherit" }}
					gap={2}
					paddingX={5}
					paddingY={2}
					backgroundColor="#E6EAEE"
				>
					<Flex gap={2}>
						<Text whiteSpace="nowrap">{htmlify(investmentLabel)}:</Text>
						<Text whiteSpace="nowrap" fontWeight="bold">
							{investmentValue}
						</Text>
					</Flex>
					<Flex gap={2}>
						<Text whiteSpace="nowrap">{htmlify(recommendedHoldingPeriodLabel)}:</Text>
						<Text whiteSpace="nowrap" fontWeight="bold">
							{section.recommendedHoldingPeriod}
						</Text>
					</Flex>
					<Flex gap={2} alignItems="center">
						<Text whiteSpace="nowrap">{htmlify(periodLabel)}</Text>
						<AccessibleSelect
							id="priips-period"
							aria-label={selectLabel ? selectLabel : "Period"}
							label={selectLabel ?? "Period"}
							value={period}
							maxWidth="150px"
							variant="lightBrand"
							onChange={(e) => {
								const value = e.target.value;

								setPeriod(value);

								const newSection = sections?.[parseInt(value, 10)];
								if (!newSection) {
									return;
								}
								setSection(newSection);
							}}
						>
							{periodOptions?.map(({ value, label }) => {
								return (
									<option
										key={value}
										value={value}
										style={{ color: "black !important", backgroundColor: "white !important" }}
									>
										{htmlify(label)}
									</option>
								);
							})}
						</AccessibleSelect>
					</Flex>
					<Flex direction="column" gap={2}>
						<Text fontWeight="bold" whiteSpace="nowrap">
							{htmlify(minimumLabel)}:
						</Text>
						<Text>{htmlify(minimumDisclaimer)}</Text>
					</Flex>
				</Flex>
			) : (
				<Flex
					direction={{ md: "column", lg: "row" }}
					alignItems={{ md: "start", lg: "center" }}
					gap={2}
					paddingX={5}
					paddingY={2}
					backgroundColor="#E6EAEE"
				>
					<Flex gap={2}>
						<Text whiteSpace="nowrap">{htmlify(investmentLabel)}</Text>
						<Text whiteSpace="nowrap" fontWeight="bold">
							{investmentValue}
						</Text>
					</Flex>
					<Flex gap={2} alignItems="center">
						<Text whiteSpace="nowrap">{htmlify(periodLabel)}</Text>
						<AccessibleSelect
							id="priips-period"
							aria-label={selectLabel ? selectLabel : "Period"}
							label={selectLabel ?? "Period"}
							value={period}
							maxWidth="150px"
							variant="lightBrand"
							onChange={(e) => {
								const value = e.target.value;

								setPeriod(value);

								const newSection = sections?.[parseInt(value, 10)];
								if (!newSection) {
									return;
								}
								setSection(newSection);
							}}
						>
							{periodOptions?.map(({ value, label }) => {
								return (
									<option
										key={value}
										value={value}
										style={{ color: "black !important", backgroundColor: "white !important" }}
									>
										{htmlify(label)}
									</option>
								);
							})}
						</AccessibleSelect>
					</Flex>
					<Flex gap={2}>
						<Text whiteSpace="nowrap">{htmlify(recommendedHoldingPeriodLabel)}</Text>
						<Text whiteSpace="nowrap" fontWeight="bold">
							{section.recommendedHoldingPeriod}
						</Text>
					</Flex>
				</Flex>
			)}
			<TableContainer>
				<Table
					variant="performanceScenarios"
					overflow="auto"
					sx={{
						borderCollapse: "separate",
						borderSpacing: "2px"
					}}
				>
					<Tbody>
						<Tr backgroundColor={{ base: "unset", md: "#F7F8FC" }}>
							<Th
								scope="row"
								whiteSpace="nowrap"
								sx={{
									...mobileProps,
									...mobileAfter,
									background: { base: "shade.100", md: "#F7F8FC" },
									fontWeight: "normal"
								}}
								borderBottom={{ base: "1px solid black", md: "unset" }}
							>
								{htmlify(scenariosLabel)}
							</Th>
							<Td borderBottom={{ base: "1px solid black", md: "unset" }} />
							{section.scenariosItems.map((scenariosItem) => {
								return (
									<Td
										borderBottom={{ base: "1px solid black", md: "unset" }}
										overflowWrap="anywhere"
										minW={{ base: "120px" }}
										whiteSpace={{ base: "break-spaces" }}
										key={scenariosItem}
									>
										{htmlify(scenariosItem)}
									</Td>
								);
							})}
						</Tr>
						{!isMobile && (
							<Tr backgroundColor="#F7F8FC">
								<Th scope="row" fontWeight="normal">
									{htmlify(minimumLabel)}
								</Th>
								<Td colSpan={section.scenariosItems.length + 1}>{htmlify(minimumDisclaimer)}</Td>
							</Tr>
						)}

						{section.categoryItems.map(({ value, label, rows }) => {
							return (
								<React.Fragment key={value}>
									{rows.map((row, i) => {
										return (
											<Tr key={i} backgroundColor={i == 1 ? "#F7F8FC" : undefined}>
												{i === 0 ? (
													<Th
														scope="row"
														_first={{ fontWeight: "bold", ...firstColumn }}
														sx={{
															py: { base: 2, md: 4 },
															px: { base: 2, md: 6 },
															overflowWrap: "anywhere",
															minWidth: { base: "100px" },
															...mobileProps,
															...mobileAfter
														}}
														rowSpan={2}
													>
														{htmlify(label)}
													</Th>
												) : null}
												{row.map((cell, j) => {
													if (j == 0) {
														return (
															<Td
																_first={{ ...firstColumn }}
																sx={{
																	py: { base: 2, md: 4 },
																	px: { base: 2, md: 6 },
																	overflowWrap: "anywhere",
																	whiteSpace: "break-spaces",
																	minWidth: { base: "120px" }
																}}
																key={j}
															>
																{htmlify(cell)}
															</Td>
														);
													}
													return (
														<Td _first={{ ...firstColumn }} key={j}>
															<HypotheticalValue value={cell} includeInvestmentAmount={false} />
														</Td>
													);
												})}
											</Tr>
										);
									})}
								</React.Fragment>
							);
						})}
					</Tbody>
				</Table>
			</TableContainer>

			<Flex direction="row" marginTop={2} flex="1" paddingY={5}>
				{disclaimer && (
					<Flex fontSize={12} color="shade.900" flex="1" flexDirection="column">
						{htmlify(interpolatePlaceholders({ text: disclaimer, instanceConfig }))}
					</Flex>
				)}
				<Spacer />
				<Button
					justifySelf="end"
					onClick={handleDownloadData}
					gap={3}
					border="1px"
					borderColor="brand.primary"
					backgroundColor="#ffffff"
					color="brand.primary"
					fontWeight="normal"
					borderRadius={3}
					_hover={{
						backgroundColor: "brand.primaryAccent",
						borderColor: "brand.primaryAccent"
					}}
					aria-description="Click to download the report as a PDF file."
				>
					<Icon as={AiOutlineDownload} />
					{htmlify(downloadDataLabel)}
				</Button>
			</Flex>
		</Flex>
	);
};
